import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TFundraiserState } from '@feathr/blackbox';
import { Button, CardV2 as Card, Skeleton } from '@feathr/components';
import { useFundraiser } from '@feathr/extender/hooks';
import stripeS from '@feathr/extender/images/integrations/stripe_s.svg';
import { cssVar } from '@feathr/hooks';

import { useStripeOnboardingRedirect } from '../../hooks/useStripeOnboardingRedirect';
import StripeConnectState from '../ConnectState';
import StripeAccountDetailsModal from './StripeAccountDetailsModal';

import * as styles from './StripeConnectCard.css';

function StripeConnectCard(): JSX.Element {
  const { t } = useTranslation();
  const { isPending: isFundraiserPending, state, fundraiser } = useFundraiser();
  const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const { redirectToStripeOnboarding } = useStripeOnboardingRedirect();

  async function handleClickCta(): Promise<void> {
    if (state === 'connected') {
      openModal();
    } else {
      await redirectToStripeOnboarding(true);
    }
  }

  function getDescriptionNode(): JSX.Element | string {
    if (isFundraiserPending) {
      return <Skeleton width={cssVar('--content-max-width-v2')} />;
    }
    return t('Connect to Stripe to create and manage donation forms');
  }

  function getCtaNode(): JSX.Element {
    if (isFundraiserPending) {
      return <Skeleton height={36} width={180} />;
    }
    const ctaTextMap: Record<TFundraiserState, string> = {
      connected: t('View details'),
      not_connected: t('Connect to Stripe'),
      pending: t('Continue setup'),
      failed: t('View details'),
    };
    return <Button onClick={handleClickCta}>{ctaTextMap[state ?? 'not_connected']}</Button>;
  }

  return (
    <Card width={'full'}>
      <Card.Header
        description={getDescriptionNode()}
        prefix={
          <img
            alt={'Purple square with rounded corners. Inside is a white s.'}
            className={styles.icon}
            src={stripeS}
          />
        }
        title={t('Payment Setup')}
      >
        {!isFundraiserPending && state && <StripeConnectState state={state} />}
        {getCtaNode()}
      </Card.Header>
      <StripeAccountDetailsModal
        onClose={closeModal}
        opened={opened}
        state={state ?? 'not_connected'}
        stripeAccountId={fundraiser?.get('stripe_connect_account')?.id ?? ''}
      />
    </Card>
  );
}

export default observer(StripeConnectCard);
